import { Form, Formik, useFormikContext } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Survey, SurveyQuestion } from '@/api/surveys';
import { FormCheckbox } from '@/components/form/FormCheckbox';
import { FormMultiSelect } from '@/components/form/FormMultiSelect';
import { FormNumberField } from '@/components/form/FormNumberField';
import { FormRatingField } from '@/components/form/FormRatingField';
import { FormSelect } from '@/components/form/FormSelect';
import { FormTextareaField } from '@/components/form/FormTextareaField';
import { FormTextField } from '@/components/form/FormTextField';
import { ArrowLeft, ArrowRight } from '@/components/icons';
import { Button } from '@/components/ui/button';
import {
    formatSurveyValueForInput,
    normalizeSurveyAnswers,
    resolveInputMode,
    resolveTextInputType,
    validateSurveyQuestionValue,
} from '@/lib/survey-validation';
import { toTitleCase } from '@/utils/toTitleCase';

interface Props {
    survey: Survey;
    onSubmit: (values: Record<string, unknown>) => void;
    onProgressChange?: (progress: number) => void;
    disabled?: boolean;
    initialData?: Record<string, unknown>;
    variant?: 'wizard' | 'flat';
}

const isBlankValue = (value: unknown) => {
    if (value === undefined || value === null) return true;
    if (typeof value === 'string') return value === '';
    if (Array.isArray(value)) return value.length === 0;
    return false;
};

const normalizeHeading = (value: string) => value.trim().toLocaleLowerCase();

const getQuestionType = (field: SurveyQuestion) =>
    (field.field_type || field.validation?.style || 'text').toLowerCase();

// Helper component to watch values and report progress
const ProgressReporter = ({
    values,
    allQuestions,
    onProgressChange,
}: {
    values: Record<string, unknown>;
    allQuestions: SurveyQuestion[];
    onProgressChange: (p: number) => void;
}) => {
    useEffect(() => {
        if (!onProgressChange) return;

        let filledCount = 0;
        allQuestions.forEach((field) => {
            const val = values[field.field_name];
            // Check if filled
            if (Array.isArray(val)) {
                if (val.length > 0) filledCount++;
            } else if (typeof val === 'boolean') {
                // Assuming boolean true counts as filled for progress if it's required or just tracked
                if (val === true) filledCount++;
            } else if (val !== undefined && val !== null && val !== '') {
                filledCount++;
            }
        });

        const progress = allQuestions.length > 0 ? (filledCount / allQuestions.length) * 100 : 0;
        onProgressChange(progress);
    }, [values, allQuestions, onProgressChange]);

    return null;
};

// Wizard Navigation and Validation Component
const WizardControls = ({
    currentSectionIndex,
    totalSections,
    onNext,
    onPrevious,
    children,
    disabled,
    currentQuestions,
    validateQuestion,
}: {
    currentSectionIndex: number;
    totalSections: number;
    onNext: () => void;
    onPrevious: () => void;
    children: React.ReactNode;
    disabled: boolean;
    currentQuestions: SurveyQuestion[];
    validateQuestion: (question: SurveyQuestion, value: unknown) => string | undefined;
}) => {
    const { values } = useFormikContext<Record<string, unknown>>();

    const isCurrentSectionValid = useMemo(() => {
        return currentQuestions.every((q) => {
            const val = values[q.field_name];
            const error = validateQuestion(q, val);

            if (!error) return true;
            if (!q.required && isBlankValue(val)) return true;
            return false;
        });
    }, [values, currentQuestions, validateQuestion]);

    const isLastSection = currentSectionIndex === totalSections - 1;
    const isFirstSection = currentSectionIndex === 0;

    return (
        <div className="mt-8 flex items-center justify-between border-t border-border pt-6">
            <div>
                {!isFirstSection && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onPrevious}
                        disabled={disabled}
                    >
                        <ArrowLeft />
                        Anterior
                    </Button>
                )}
            </div>

            <div className="flex flex-1 justify-end">
                {!isLastSection ? (
                    <Button
                        type="button"
                        onClick={onNext}
                        disabled={disabled || !isCurrentSectionValid}
                    >
                        Próximo
                        <ArrowRight className="ml-2 size-4" />
                    </Button>
                ) : (
                    <div className="w-full md:w-auto">{children}</div>
                )}
            </div>
        </div>
    );
};

export default function DynamicSurveyForm({
    survey,
    onSubmit,
    onProgressChange,
    children,
    disabled = false,
    initialData,
    variant = 'wizard',
}: React.PropsWithChildren<Props>) {
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const topRef = useRef<HTMLDivElement | null>(null);
    const hasNavigatedRef = useRef(false);

    const allQuestions = useMemo(() => {
        return survey.sections?.flatMap((s) => s.questions) || [];
    }, [survey]);

    const validateQuestion = (question: SurveyQuestion, value: unknown) => {
        return validateSurveyQuestionValue(question, value);
    };

    const defaultValues = useMemo(() => {
        return allQuestions.reduce(
            (acc, field) => {
                const type = getQuestionType(field);
                if (type === 'checkbox') acc[field.field_name] = false;
                else if (type === 'multiselect') acc[field.field_name] = [];
                else acc[field.field_name] = '';
                return acc;
            },
            {} as Record<string, unknown>,
        );
    }, [allQuestions]);

    const initialValues = useMemo(() => {
        const mergedValues = {
            ...defaultValues,
            ...(initialData || {}),
        } as Record<string, unknown>;

        allQuestions.forEach((question) => {
            mergedValues[question.field_name] = formatSurveyValueForInput(
                mergedValues[question.field_name],
                question.validation,
            );
        });

        return mergedValues;
    }, [allQuestions, defaultValues, initialData]);

    const handleNext = () => {
        if (currentSectionIndex < (survey.sections?.length || 0) - 1) {
            hasNavigatedRef.current = true;
            setCurrentSectionIndex((prev) => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentSectionIndex > 0) {
            hasNavigatedRef.current = true;
            setCurrentSectionIndex((prev) => prev - 1);
        }
    };

    useEffect(() => {
        if (!hasNavigatedRef.current) return;
        topRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
        hasNavigatedRef.current = false;
    }, [currentSectionIndex]);

    const renderQuestions = (questions: SurveyQuestion[]) => {
        return questions.map((field, index) => {
            const type = getQuestionType(field);
            const commonProps = {
                name: field.field_name,
                label: field.field_label || toTitleCase(field.field_name.replace(/_/g, ' ')),
                helpText: field.config?.help_text,
                required: field.required,
                disabled: disabled,
                validate: (value: unknown) => validateQuestion(field, value),
            };

            let options: { label: string; value: string }[] = [];
            if (field.options) {
                options = field.options.map((opt) =>
                    typeof opt === 'string' ? { label: opt, value: opt } : opt,
                );
            }

            const hasOtherOption = options.some(
                (opt) =>
                    opt.label.toLowerCase().includes('outro') ||
                    opt.label.toLowerCase().includes('outros'),
            );
            const shouldShowOtherOption = Boolean(field.config?.allow_other) || hasOtherOption;

            if (field.config?.allow_other && !hasOtherOption) {
                options = [...options, { label: 'Outro', value: 'other' }];
            }

            switch (type) {
                case 'checkbox':
                    return <FormCheckbox key={index} {...commonProps} />;
                case 'textfield':
                case 'text':
                    return (
                        <FormTextField
                            key={index}
                            {...commonProps}
                            placeholder={field.config?.placeholder}
                            type={resolveTextInputType(field.validation)}
                            inputMode={resolveInputMode(field.validation)}
                        />
                    );
                case 'textarea':
                case 'long_text':
                    return (
                        <FormTextareaField
                            key={index}
                            {...commonProps}
                            placeholder={field.config?.placeholder}
                        />
                    );
                case 'select':
                    return (
                        <FormSelect
                            key={index}
                            {...commonProps}
                            options={options}
                            placeholder={field.config?.placeholder}
                            showOtherOption={shouldShowOtherOption}
                        />
                    );
                case 'multiselect':
                    return (
                        <FormMultiSelect
                            key={index}
                            {...commonProps}
                            options={options}
                            placeholder={field.config?.placeholder}
                        />
                    );
                case 'rating':
                    return (
                        <FormRatingField
                            key={index}
                            {...commonProps}
                            config={field.config?.rating}
                        />
                    );
                case 'number':
                case 'number field':
                    return (
                        <FormNumberField
                            key={index}
                            {...commonProps}
                            placeholder={field.config?.placeholder}
                            inputMode={resolveInputMode(field.validation)}
                        />
                    );
                default:
                    return (
                        <FormTextField
                            key={index}
                            {...commonProps}
                            placeholder={field.config?.placeholder}
                            type={resolveTextInputType(field.validation)}
                            inputMode={resolveInputMode(field.validation)}
                        />
                    );
            }
        });
    };

    if (!survey.sections || survey.sections.length === 0) return null;

    const shouldHideRepeatedSectionTitle =
        survey.sections.length === 1 &&
        normalizeHeading(survey.sections[0].title) === normalizeHeading(survey.name);

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values) => {
                const normalizedValues = normalizeSurveyAnswers(
                    allQuestions,
                    values as Record<string, unknown>,
                );
                onSubmit(normalizedValues);
            }}
        >
            {({ values }) => (
                <Form className="space-y-6">
                    {onProgressChange && (
                        <ProgressReporter
                            values={values}
                            allQuestions={allQuestions}
                            onProgressChange={onProgressChange}
                        />
                    )}

                    {variant === 'flat' ? (
                        // Flat View (Stacked Sections)
                        <div className="space-y-12">
                            {survey.sections.map((section, index) => {
                                const hideSectionTitle =
                                    shouldHideRepeatedSectionTitle && index === 0;

                                return (
                                    <div
                                        key={section.id}
                                        id={`survey-section-${section.id}`}
                                        className="scroll-mt-28 space-y-6 rounded-xl border border-border bg-card p-6 shadow-sm"
                                    >
                                        {(!hideSectionTitle || section.description) && (
                                            <div className="mb-4 border-b border-border pb-2">
                                                {!hideSectionTitle && (
                                                    <h2 className="text-xl font-semibold text-foreground">
                                                        {section.title}
                                                    </h2>
                                                )}
                                                {section.description && (
                                                    <p className="text-muted-foreground">
                                                        {section.description}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        <div className="space-y-4">
                                            {renderQuestions(section.questions)}
                                        </div>
                                    </div>
                                );
                            })}
                            <div className="mt-8 flex justify-end border-t border-border pt-6">
                                {children}
                            </div>
                        </div>
                    ) : (
                        // Wizard View
                        <>
                            <div ref={topRef} />
                            {(!shouldHideRepeatedSectionTitle ||
                                survey.sections[currentSectionIndex].description) && (
                                <div className="mb-6 space-y-2 border-b border-border pb-4">
                                    {!shouldHideRepeatedSectionTitle && (
                                        <h2 className="text-xl font-semibold text-foreground">
                                            {survey.sections[currentSectionIndex].title}
                                        </h2>
                                    )}
                                    {survey.sections[currentSectionIndex].description && (
                                        <p className="text-muted-foreground">
                                            {survey.sections[currentSectionIndex].description}
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className="min-h-[200px] space-y-4">
                                {renderQuestions(survey.sections[currentSectionIndex].questions)}
                            </div>

                            <WizardControls
                                currentSectionIndex={currentSectionIndex}
                                totalSections={survey.sections.length}
                                onNext={handleNext}
                                onPrevious={handlePrevious}
                                disabled={disabled}
                                currentQuestions={survey.sections[currentSectionIndex].questions}
                                validateQuestion={validateQuestion}
                            >
                                {children}
                            </WizardControls>
                        </>
                    )}
                </Form>
            )}
        </Formik>
    );
}
